import React, { useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Delete } from '@mui/icons-material';

import { useAlert } from '../../context/AlertContext';
import { showConfirmDialogComponent } from '../../core/ConfirmDialog';
import {
  IBookingBusinessHours,
  WEEKDAYS,
} from '../../Interfaces/IBookingBusinessHours';
import {
  useCreateBookingHours,
  useDeleteBookingHours,
  useGetBookingHours,
  useUpdateBookingHours,
} from '../../hooks/reactQuerys/BookingBusinessHoursQueries';
import { useGetLocations } from '../../hooks/reactQuerys/LocationQueries';
import { useGetMeetingTypes } from '../../hooks/reactQuerys/MeetingTypeQueries';
import { useGetRooms } from '../../hooks/reactQuerys/RoomQueries';
import { useGetUsers } from '../../hooks/reactQuerys/UserQueries';
import { useCalendarConnections } from '../../hooks/reactQuerys/CalendarConnectionQueries';

const weekdayLabel = (weekday: number): string =>
  WEEKDAYS.find(day => day.value === weekday)?.label ?? String(weekday);

const toTimeInput = (value: string): string => (value ? value.slice(0, 5) : '');

const numberOrNull = (value: string): number | null =>
  value.trim() === '' ? null : Number(value);

interface IHoursBlock {
  idBookingBusinessHours: number; // <0 = neu
  FromTime: string;
  ToTime: string;
}

interface IHoursEditState {
  Weekday: number;
  idLocation: number | null;
  idMeetingType: number | null;
  idUser: number | null;
  blocks: IHoursBlock[];
  // Ausgangs-IDs desselben Scopes+Wochentags — fuer die Loesch-Diff.
  originalIds: number[];
}

const sameScope = (
  a: IBookingBusinessHours,
  scope: Pick<
    IHoursEditState,
    'Weekday' | 'idLocation' | 'idMeetingType' | 'idUser'
  >
): boolean =>
  a.Weekday === scope.Weekday &&
  a.idLocation === scope.idLocation &&
  a.idMeetingType === scope.idMeetingType &&
  a.idUser === scope.idUser;

export const BookingHoursEditor: React.FC = () => {
  const theme = useTheme();
  const { showAlert } = useAlert();
  const { bookingHours, isLoadingBookingHours, refetchBookingHours } =
    useGetBookingHours();
  const { createBookingHours, isPendingCreateBookingHours } =
    useCreateBookingHours();
  const { updateBookingHours, isPendingUpdateBookingHours } =
    useUpdateBookingHours();
  const { deleteBookingHours, isPendingDeleteBookingHours } =
    useDeleteBookingHours();
  const { locations } = useGetLocations();
  const { meetingTypes } = useGetMeetingTypes();
  const { users } = useGetUsers();
  const { rooms } = useGetRooms();

  const [edit, setEdit] = useState<IHoursEditState | null>(null);

  // Im Dialog gewählte Terminart (für die lesende Vorschau rechts).
  const selectedType =
    edit && edit.idMeetingType !== null
      ? meetingTypes.find(mt => mt.idMeetingType === edit.idMeetingType)
      : undefined;
  // Kalenderverbindungen des zuständigen Mitarbeiters der Terminart (nur Anzeige).
  const { connections: typeConnections } = useCalendarConnections(
    selectedType?.idUser ?? 0
  );

  const busy =
    isPendingCreateBookingHours ||
    isPendingUpdateBookingHours ||
    isPendingDeleteBookingHours;

  const locationName = (idLocation: number | null): string =>
    idLocation === null
      ? 'global'
      : (locations?.find(location => location.idLocation === idLocation)
          ?.Location ?? String(idLocation));

  const meetingTypeName = (idMeetingType: number | null): string =>
    idMeetingType === null
      ? 'alle Terminarten'
      : (meetingTypes.find(mt => mt.idMeetingType === idMeetingType)?.Name ??
        String(idMeetingType));

  // Nur-lesende Namen für die Terminart-Vorschau ('—' bei null).
  const dash = '—';
  const typeLocationName = (id: number | null): string =>
    id == null
      ? dash
      : (locations?.find(l => l.idLocation === id)?.Location ?? dash);
  const typeRoomName = (id: number | null): string =>
    id == null ? dash : (rooms?.find(r => r.idRoom === id)?.Name ?? dash);
  const typeUserName = (id: number | null): string => {
    if (id == null) return dash;
    const u = users?.find(x => x.idUser === id);
    return u ? `${u.FirstName} ${u.LastName}` : dash;
  };
  const typeConnectionName = (id: number | null): string =>
    id == null
      ? dash
      : (typeConnections?.find(c => c.idUserCalendarConnection === id)?.Label ??
        dash);
  const previewRow = (label: string, value: React.ReactNode) => (
    <Stack direction='row' spacing={1} justifyContent='space-between'>
      <Typography variant='body2' color='text.secondary'>
        {label}
      </Typography>
      <Typography variant='body2' sx={{ fontWeight: 500, textAlign: 'right' }}>
        {value}
      </Typography>
    </Stack>
  );

  const openNew = () =>
    setEdit({
      Weekday: 1,
      idLocation: null,
      idMeetingType: null,
      idUser: null,
      blocks: [
        {
          idBookingBusinessHours: -1,
          FromTime: '09:00:00',
          ToTime: '17:00:00',
        },
      ],
      originalIds: [],
    });

  const openFromRow = (row: IBookingBusinessHours) => {
    const scope = {
      Weekday: row.Weekday,
      idLocation: row.idLocation,
      idMeetingType: row.idMeetingType,
      idUser: row.idUser,
    };
    const group = bookingHours.filter(r => sameScope(r, scope));
    setEdit({
      ...scope,
      blocks: group.map(r => ({
        idBookingBusinessHours: r.idBookingBusinessHours,
        FromTime: r.FromTime,
        ToTime: r.ToTime,
      })),
      originalIds: group.map(r => r.idBookingBusinessHours),
    });
  };

  const patchScope = (changes: Partial<IHoursEditState>) =>
    setEdit(current => (current ? { ...current, ...changes } : current));

  const patchBlock = (index: number, changes: Partial<IHoursBlock>) =>
    setEdit(current =>
      current
        ? {
            ...current,
            blocks: current.blocks.map((b, i) =>
              i === index ? { ...b, ...changes } : b
            ),
          }
        : current
    );

  const addBlock = () =>
    setEdit(current =>
      current
        ? {
            ...current,
            blocks: [
              ...current.blocks,
              {
                idBookingBusinessHours: -1,
                FromTime: '09:00:00',
                ToTime: '17:00:00',
              },
            ],
          }
        : current
    );

  const removeBlock = (index: number) =>
    setEdit(current =>
      current
        ? { ...current, blocks: current.blocks.filter((_, i) => i !== index) }
        : current
    );

  const handleSave = async () => {
    if (!edit) return;
    if (edit.blocks.length === 0) {
      showAlert({ severity: 'error', text: 'Mindestens ein Zeitblock nötig.' });
      return;
    }
    for (const b of edit.blocks) {
      if (toTimeInput(b.FromTime) >= toTimeInput(b.ToTime)) {
        showAlert({
          severity: 'error',
          text: 'Jeder Block: Von muss vor Bis liegen.',
        });
        return;
      }
    }
    const scopeFields = {
      Weekday: edit.Weekday,
      idLocation: edit.idLocation,
      idMeetingType: edit.idMeetingType,
      idUser: edit.idUser,
    };
    const keptIds = edit.blocks
      .filter(b => b.idBookingBusinessHours >= 0)
      .map(b => b.idBookingBusinessHours);
    const toDelete = edit.originalIds.filter(id => !keptIds.includes(id));
    try {
      for (const b of edit.blocks) {
        if (b.idBookingBusinessHours < 0) {
          await createBookingHours({
            ...scopeFields,
            FromTime: b.FromTime,
            ToTime: b.ToTime,
          });
        } else {
          await updateBookingHours({
            idBookingBusinessHours: b.idBookingBusinessHours,
            ...scopeFields,
            FromTime: b.FromTime,
            ToTime: b.ToTime,
          });
        }
      }
      for (const id of toDelete) {
        await deleteBookingHours(id);
      }
      showAlert({ severity: 'success', text: 'Zeitfenster gespeichert.' });
      setEdit(null);
      void refetchBookingHours();
    } catch {
      showAlert({ severity: 'error', text: 'Speichern fehlgeschlagen.' });
      // Nach Teil-Erfolg (einige Bloecke wurden bereits angelegt/aktualisiert)
      // den Serverstand neu laden, damit ein erneutes Speichern nicht dieselben
      // Bloecke ein zweites Mal anlegt (Diff gegen echte IDs statt gegen -1).
      void refetchBookingHours();
    }
  };

  const handleDelete = async (id: number) => {
    const confirmed = await showConfirmDialogComponent(
      'Zeitfenster löschen',
      'Dieses buchbare Zeitfenster wirklich löschen?',
      'Löschen',
      'Abbrechen',
      theme
    );
    if (!confirmed) {
      return;
    }
    try {
      await deleteBookingHours(id);
      showAlert({ severity: 'success', text: 'Zeitfenster gelöscht.' });
      void refetchBookingHours();
    } catch {
      showAlert({ severity: 'error', text: 'Löschen fehlgeschlagen.' });
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        sx={{ mb: 1 }}
      >
        <Typography variant='h6'>Buchbare Zeiten</Typography>
        <Button variant='contained' onClick={openNew}>
          Zeitfenster hinzufügen
        </Button>
      </Stack>

      <Alert severity='info' sx={{ mb: 2 }}>
        Ohne Standort gilt das Zeitfenster global. Ohne Terminart gilt es für
        alle Terminarten. Spezifische Standort-/Terminart-Zeiten überschreiben
        globale Zeiten.
      </Alert>

      {isLoadingBookingHours && <CircularProgress size={24} />}

      {!isLoadingBookingHours && bookingHours.length === 0 && (
        <Typography color='text.secondary'>
          Noch keine buchbaren Zeiten konfiguriert.
        </Typography>
      )}

      {bookingHours.length > 0 && (
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Wochentag</TableCell>
              <TableCell>Standort</TableCell>
              <TableCell>Von</TableCell>
              <TableCell>Bis</TableCell>
              <TableCell>Terminart</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {bookingHours.map(row => (
              <TableRow
                key={row.idBookingBusinessHours}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => openFromRow(row)}
              >
                <TableCell>{weekdayLabel(row.Weekday)}</TableCell>
                <TableCell>{locationName(row.idLocation)}</TableCell>
                <TableCell>{toTimeInput(row.FromTime)}</TableCell>
                <TableCell>{toTimeInput(row.ToTime)}</TableCell>
                <TableCell>{meetingTypeName(row.idMeetingType)}</TableCell>
                <TableCell align='right'>
                  <IconButton
                    size='small'
                    disabled={busy}
                    onClick={event => {
                      event.stopPropagation();
                      void handleDelete(row.idBookingBusinessHours);
                    }}
                  >
                    <Delete fontSize='small' />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog
        open={edit !== null}
        onClose={() => setEdit(null)}
        fullWidth
        maxWidth={selectedType ? 'md' : 'sm'}
      >
        <DialogTitle>
          {edit && edit.originalIds.length === 0
            ? 'Zeitfenster hinzufügen'
            : 'Zeitfenster bearbeiten'}
        </DialogTitle>
        {edit && (
          <DialogContent>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              sx={{ mt: 1 }}
              alignItems='flex-start'
            >
              <Stack spacing={2} sx={{ flex: 1, width: '100%', minWidth: 0 }}>
                <FormControl size='small' fullWidth>
                  <InputLabel id='bh-weekday-label'>Wochentag</InputLabel>
                  <Select
                    labelId='bh-weekday-label'
                    label='Wochentag'
                    value={String(edit.Weekday)}
                    onChange={event =>
                      patchScope({ Weekday: Number(event.target.value) })
                    }
                  >
                    {WEEKDAYS.map(day => (
                      <MenuItem key={day.value} value={String(day.value)}>
                        {day.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Stack spacing={1}>
                  <Typography variant='subtitle2'>Zeitblöcke</Typography>
                  {edit.blocks.map((block, index) => (
                    <Stack
                      direction='row'
                      spacing={1}
                      alignItems='center'
                      key={index}
                    >
                      <TextField
                        label='Von'
                        type='time'
                        value={toTimeInput(block.FromTime)}
                        onChange={event =>
                          patchBlock(index, { FromTime: event.target.value })
                        }
                        size='small'
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        label='Bis'
                        type='time'
                        value={toTimeInput(block.ToTime)}
                        onChange={event =>
                          patchBlock(index, { ToTime: event.target.value })
                        }
                        size='small'
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                      <IconButton
                        size='small'
                        disabled={edit.blocks.length <= 1}
                        onClick={() => removeBlock(index)}
                      >
                        <Delete fontSize='small' />
                      </IconButton>
                    </Stack>
                  ))}
                  <Button size='small' onClick={addBlock}>
                    + Block hinzufügen
                  </Button>
                </Stack>

                <FormControl size='small' fullWidth>
                  <InputLabel id='bh-location-label'>Standort</InputLabel>
                  <Select
                    labelId='bh-location-label'
                    label='Standort'
                    value={
                      edit.idLocation === null ? '' : String(edit.idLocation)
                    }
                    onChange={event =>
                      patchScope({
                        idLocation: numberOrNull(event.target.value),
                      })
                    }
                  >
                    <MenuItem value=''>— global (alle Standorte) —</MenuItem>
                    {(locations ?? []).map(location => (
                      <MenuItem
                        key={location.idLocation}
                        value={String(location.idLocation)}
                      >
                        {location.Location}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size='small' fullWidth>
                  <InputLabel id='bh-meetingtype-label'>Terminart</InputLabel>
                  <Select
                    labelId='bh-meetingtype-label'
                    label='Terminart'
                    value={
                      edit.idMeetingType === null
                        ? ''
                        : String(edit.idMeetingType)
                    }
                    onChange={event =>
                      patchScope({
                        idMeetingType: numberOrNull(event.target.value),
                      })
                    }
                  >
                    <MenuItem value=''>— alle Terminarten —</MenuItem>
                    {meetingTypes.map(mt => (
                      <MenuItem
                        key={mt.idMeetingType}
                        value={String(mt.idMeetingType)}
                      >
                        {mt.Name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size='small' fullWidth>
                  <InputLabel id='bh-user-label'>Notar/Mitarbeiter</InputLabel>
                  <Select
                    labelId='bh-user-label'
                    label='Notar/Mitarbeiter'
                    value={edit.idUser === null ? '' : String(edit.idUser)}
                    onChange={event =>
                      patchScope({ idUser: numberOrNull(event.target.value) })
                    }
                  >
                    <MenuItem value=''>— alle —</MenuItem>
                    {(users ?? []).map(user => (
                      <MenuItem key={user.idUser} value={String(user.idUser)}>
                        {user.FirstName} {user.LastName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              {selectedType && (
                <Box
                  sx={{
                    flex: 1,
                    width: '100%',
                    alignSelf: 'stretch',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    p: 2,
                    backgroundColor: 'action.hover',
                  }}
                >
                  <Typography
                    variant='caption'
                    color='primary'
                    sx={{ fontWeight: 600, textTransform: 'uppercase' }}
                  >
                    Terminart – Vorschau (nur lesend)
                  </Typography>
                  <Typography variant='h6' sx={{ mt: 0.5, mb: 1 }}>
                    {selectedType.Name}
                  </Typography>
                  <Divider sx={{ mb: 1.5 }} />
                  <Stack spacing={0.75}>
                    {previewRow('Dauer', `${selectedType.DurationInMin} Min.`)}
                    {previewRow(
                      'Puffer (vor/nach)',
                      `${selectedType.BufferBeforeMin ?? 0} / ${
                        selectedType.BufferAfterMin ?? 0
                      } Min.`
                    )}
                    {previewRow(
                      'Slot-Takt',
                      selectedType.SlotStepInMin
                        ? `${selectedType.SlotStepInMin} Min.`
                        : 'Standard (15 Min.)'
                    )}
                    {previewRow(
                      'Buchungshorizont',
                      selectedType.BookingHorizonDays
                        ? `${selectedType.BookingHorizonDays} Tage`
                        : 'Standard'
                    )}
                    {previewRow(
                      'Standard-Standort',
                      typeLocationName(selectedType.idLocation)
                    )}
                    {previewRow(
                      'Standard-Raum',
                      typeRoomName(selectedType.idRoom)
                    )}
                    {previewRow(
                      'Zuständiger Mitarbeiter',
                      typeUserName(selectedType.idUser)
                    )}
                    {previewRow(
                      'Kalenderverbindung',
                      typeConnectionName(selectedType.idUserCalendarConnection)
                    )}
                  </Stack>
                  <Stack direction='row' spacing={1} sx={{ mt: 1.5 }}>
                    <Chip
                      size='small'
                      variant='outlined'
                      color={selectedType.IsActive ? 'success' : 'default'}
                      label={selectedType.IsActive ? 'Aktiv' : 'Inaktiv'}
                    />
                    {selectedType.AllowParallel && (
                      <Chip
                        size='small'
                        variant='outlined'
                        color='info'
                        label='Darf parallel laufen'
                      />
                    )}
                  </Stack>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ display: 'block', mt: 1.5 }}
                  >
                    Ändern unter Einstellungen → Termine → Terminarten.
                  </Typography>
                </Box>
              )}
            </Stack>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setEdit(null)} disabled={busy}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            onClick={() => void handleSave()}
            disabled={busy}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BookingHoursEditor;
